import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type Chatbot } from 'entities/Chatbot'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  EnrollmentFilters,
  EnrollmentStore,
  initEnrollmentFilters,
} from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'

export class ChatbotEnrollment {
  private _enrollmentStore: EnrollmentStore
  public enrollmentFilters = EnrollmentFilters.Empty()
  public enrollmentExcludeFilters = EnrollmentFilters.Empty()
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(private _chatbot: Chatbot, private _entities: IEntitiesStore) {
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'chatbot',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
        excludeSearchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )

    makeAutoObservable(this)

    this.reactionIsShowContactsAlert()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert?.()
    this._disposeIsShowContactsAlert = reaction(
      () => [this._enrollmentStore.isContactReEnroll, this.enrollmentFilters.filters],
      () => this._enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    )
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  initFilters = () => this._enrollmentStore.initFilters()

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()
    const excludeFilters = this.enrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)
    const confirmFilters: IEnrollmentActions['confirm'] = async ({ filters, excludeFilters }) => {
      this.enrollmentFilters = filters
      this.enrollmentExcludeFilters = excludeFilters

      this._chatbot.filters.syncTagIds(this.enrollmentFilters.tags)
      this._chatbot.filters.syncContactIds(this.enrollmentFilters.contacts)
      this._chatbot.filters.syncSegmentIds(this.enrollmentFilters.segments)
      this._chatbot.filters.syncAdvanced(this.enrollmentFilters.filters)

      this._chatbot.excludeFilters.syncTagIds(this.enrollmentExcludeFilters.tags)
      this._chatbot.excludeFilters.syncContactIds(this.enrollmentExcludeFilters.contacts)
      this._chatbot.excludeFilters.syncSegmentIds(this.enrollmentExcludeFilters.segments)
      this._chatbot.excludeFilters.syncAdvanced(this.enrollmentExcludeFilters.filters)

      return true
    }

    this._enrollmentStore.openModal({
      title: 'Contacts',
      allowEmptyCount: true,
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
    })
  }

  syncFilters = async () => {
    const filters = this._chatbot.filters.toJSON()
    const exclude_filters = this._chatbot.excludeFilters.toJSON()

    const [filtersData, excludeFiltersData] = await Promise.all([
      initEnrollmentFilters(this._entities, filters),
      initEnrollmentFilters(this._entities, exclude_filters),
    ])

    if (filtersData) {
      this.enrollmentFilters.sync(filtersData)
    }

    if (excludeFiltersData) {
      this.enrollmentExcludeFilters.sync(excludeFiltersData)
    }

    this._enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    this._enrollmentStore.setIsContactReEnroll(this._chatbot.settings.allowReEntering)
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
  }
}
